import { getAccessToken } from '../lib/authSession';
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Fingerprint, 
  ScanFace, 
  Key, 
  Shield, 
  Check, 
  X, 
  Loader2, 
  AlertTriangle,
  Smartphone,
  Settings,
  Trash2
} from 'lucide-react';
import { webAuthnService } from '../services/webAuthnService';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * Biometric Signature Component
 * Provides advanced electronic signature with biometric verification
 */
const BiometricSignature = ({ 
  contractId, 
  contractTitle,
  onSignatureComplete,
  onCancel 
}) => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [authenticators, setAuthenticators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [step, setStep] = useState('check'); // check, register, authenticate, success
  const [error, setError] = useState(null);
  const [signatureData, setSignatureData] = useState(null);

  // Check WebAuthn support and credentials on mount
  useEffect(() => {
    checkBiometricSupport();
  }, []);

  const checkBiometricSupport = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const supported = webAuthnService.isSupported;
      setIsSupported(supported);

      if (supported) {
        const [auths, hasCreds] = await Promise.all([
          webAuthnService.getAvailableAuthenticators(),
          webAuthnService.hasRegisteredCredentials()
        ]);
        
        setAuthenticators(auths);
        setHasCredentials(hasCreds);
        
        if (hasCreds) {
          setStep('authenticate');
        } else {
          setStep('register');
        }
      } else {
        setStep('unsupported');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    setIsRegistering(true);
    setError(null);

    try {
      await webAuthnService.registerCredential(
        user.id,
        user.email,
        user.username || user.email
      );
      
      setHasCredentials(true);
      setStep('authenticate');
      toast.success('Biometría registrada exitosamente');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleAuthenticate = async () => {
    setIsAuthenticating(true);
    setError(null);

    try {
      const result = await webAuthnService.authenticate(contractId);
      
      setSignatureData({
        type: 'biometric',
        verification_id: result.verification_id,
        timestamp: new Date().toISOString(),
        authenticator_type: result.authenticator_type,
        user_verified: result.user_verified
      });
      
      setStep('success');
      
      // Notify parent after a brief delay to show success state
      setTimeout(() => {
        onSignatureComplete?.({
          ...result,
          signature_type: 'biometric_advanced',
          contract_id: contractId
        });
      }, 1500);
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const getAuthenticatorIcon = (type) => {
    switch (type) {
      case 'fingerprint':
        return <Fingerprint className="w-6 h-6" />;
      case 'scan-face':
        return <ScanFace className="w-6 h-6" />;
      case 'key':
        return <Key className="w-6 h-6" />;
      default:
        return <Shield className="w-6 h-6" />;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 text-center">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
        <p className="text-slate-400">Verificando soporte biométrico...</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-blue-500/10 to-purple-500/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Shield className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Firma Electrónica Avanzada</h3>
              <p className="text-sm text-slate-400">Verificación biométrica segura</p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Contract Info */}
        {contractTitle && (
          <div className="mb-6 p-4 bg-slate-900/50 rounded-lg">
            <p className="text-sm text-slate-400">Documento a firmar:</p>
            <p className="text-white font-medium">{contractTitle}</p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-red-400/80">{error}</p>
            </div>
          </div>
        )}

        {/* Unsupported State */}
        {step === 'unsupported' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-400" />
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">
              Biometría no disponible
            </h4>
            <p className="text-slate-400 mb-6">
              Tu navegador no soporta autenticación biométrica.
              Usa un navegador moderno (Chrome, Safari, Edge, Firefox) en un dispositivo compatible.
            </p>
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            >
              Usar firma tradicional
            </button>
          </div>
        )}

        {/* Register Step */}
        {step === 'register' && (
          <div className="text-center py-4">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Fingerprint className="w-10 h-10 text-blue-400" />
            </div>
            
            <h4 className="text-xl font-semibold text-white mb-2">
              Registrar Biometría
            </h4>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Para firmar documentos de forma segura, primero debes registrar tu huella digital o reconocimiento facial.
            </p>

            {/* Available Authenticators */}
            <div className="mb-6 space-y-2">
              {authenticators.map((auth, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg mx-auto max-w-xs"
                >
                  <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                    {getAuthenticatorIcon(auth.icon)}
                  </div>
                  <span className="text-white">{auth.name}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleRegister}
              disabled={isRegistering}
              className="px-8 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-500 disabled:opacity-50 transition-all flex items-center gap-2 mx-auto"
            >
              {isRegistering ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Registrando...
                </>
              ) : (
                <>
                  <Fingerprint className="w-5 h-5" />
                  Registrar Biometría
                </>
              )}
            </button>

            <p className="text-xs text-slate-500 mt-4">
              Tu biometría se almacena de forma segura en tu dispositivo.
              NotaryOS nunca tiene acceso a tus datos biométricos.
            </p>
          </div>
        )}

        {/* Authenticate Step */}
        {step === 'authenticate' && (
          <div className="text-center py-4">
            <div className="w-24 h-24 bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6 relative">
              <ScanFace className="w-12 h-12 text-green-400" />
              {isAuthenticating && (
                <div className="absolute inset-0 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
              )}
            </div>
            
            <h4 className="text-xl font-semibold text-white mb-2">
              Verificar Identidad
            </h4>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Usa tu huella digital o reconocimiento facial para firmar el documento de forma segura.
            </p>

            <button
              onClick={handleAuthenticate}
              disabled={isAuthenticating}
              className="px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-500 hover:to-blue-500 disabled:opacity-50 transition-all flex items-center gap-3 mx-auto text-lg shadow-lg shadow-green-500/20"
            >
              {isAuthenticating ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <Fingerprint className="w-6 h-6" />
                  Firmar con Biometría
                </>
              )}
            </button>

            <div className="mt-6 flex items-center justify-center gap-4 text-sm">
              <button
                onClick={() => setStep('register')}
                className="text-slate-400 hover:text-white flex items-center gap-1"
              >
                <Settings className="w-4 h-4" />
                Configurar otra biometría
              </button>
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="text-center py-8">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-green-400" />
            </div>
            
            <h4 className="text-xl font-semibold text-green-400 mb-2">
              ¡Firma Exitosa!
            </h4>
            <p className="text-slate-400 mb-4">
              El documento ha sido firmado con verificación biométrica.
            </p>

            {signatureData && (
              <div className="bg-slate-900/50 rounded-lg p-4 max-w-sm mx-auto text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tipo:</span>
                    <span className="text-white">Firma Biométrica Avanzada</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Verificado:</span>
                    <span className="text-green-400">
                      {signatureData.user_verified ? 'Sí' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Fecha:</span>
                    <span className="text-white">
                      {new Date(signatureData.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-slate-700 bg-slate-900/50">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>Protegido con WebAuthn / FIDO2</span>
          </div>
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4" />
            <span>Compatible con Face ID y Touch ID</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Biometric Settings Component
 * For managing registered biometric credentials
 */
export const BiometricSettings = () => {
  const [credentials, setCredentials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setIsLoading(true);
    try {
      setIsSupported(webAuthnService.isSupported);
      
      const token = getAccessToken();
      if (token) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webauthn/credentials`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setCredentials(data.credentials || []);
        }
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async (credentialId) => {
    try {
      await webAuthnService.removeCredential(credentialId);
      setCredentials(prev => prev.filter(c => c.id !== credentialId));
      toast.success('Credencial eliminada');
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!isSupported) {
    return (
      <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
        <p className="text-sm text-orange-400">
          La autenticación biométrica no está disponible en este navegador.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Credenciales Biométricas</h4>
        <BiometricRegisterButton onRegistered={loadCredentials} />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando...
        </div>
      ) : credentials.length === 0 ? (
        <p className="text-sm text-slate-400">
          No tienes credenciales biométricas registradas.
        </p>
      ) : (
        <div className="space-y-2">
          {credentials.map((cred) => (
            <div 
              key={cred.id}
              className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Fingerprint className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-sm text-white">{cred.name || 'Biometría'}</p>
                  <p className="text-xs text-slate-500">
                    Registrado: {new Date(cred.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleRemove(cred.id)}
                className="p-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Small button to register biometric
 */
export const BiometricRegisterButton = ({ onRegistered }) => {
  const { user } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);

  const handleRegister = async () => {
    setIsRegistering(true);
    try {
      await webAuthnService.registerCredential(
        user.id,
        user.email,
        user.username || user.email
      );
      toast.success('Biometría registrada');
      onRegistered?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsRegistering(false);
    }
  };

  if (!webAuthnService.isSupported) return null;

  return (
    <button
      onClick={handleRegister}
      disabled={isRegistering}
      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50"
    >
      {isRegistering ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Fingerprint className="w-4 h-4" />
      )}
      Agregar
    </button>
  );
};

export default BiometricSignature;
